import { Accessor, batch, createMemo, createSignal } from 'solid-js';
import { ControlMenuItemType } from '../control-menu';
import {
  findActionItemById,
  findCheckboxItemById,
  findRadioGroupForOptionId,
  getActiveCheckboxIds,
  getDefaultItemId,
  isReplayableItemId,
  isStoredMultipleSelectionItemId,
} from './last-tool-selection-menu.utils';
import {
  readStoredSelections,
  writeStoredSelections,
} from './last-tool-selection-storage';
import { wrapItemsForSelectionTracking } from './wrap-items-for-selection-tracking';

export interface UseLastToolSelectionOptions {
  items: Accessor<ControlMenuItemType[]>;
  storageKey: Accessor<string>;
  /** When true, persists an array of selected item ids (e.g. multi-checkbox menus). */
  multipleSelection?: Accessor<boolean>;
}

export interface UseLastToolSelectionResult {
  /** Items wrapped so any selection is recorded for later replay. */
  readonly menuItems: Accessor<ControlMenuItemType[]>;
  /** Replays the last recorded selection, falling back to the first actionable item. */
  activateLastSelection(): void;
}

/**
 * @intent Tracks the last tool the user picked from a split dropdown so the
 * primary (icon) button can replay it. Persists the choice in sessionStorage
 * and wraps menu items so nested selections record their owning action.
 */
export const useLastToolSelection = (
  options: UseLastToolSelectionOptions
): UseLastToolSelectionResult => {
  const isMultipleSelection = (): boolean =>
    options.multipleSelection?.() ?? false;

  const [lastSelectedItemIds, setLastSelectedItemIds] = createSignal<string[]>(
    readStoredSelections(options.storageKey(), isMultipleSelection())
  );

  const persistSelections = (itemIds: string[]): void => {
    setLastSelectedItemIds(itemIds);
    writeStoredSelections(options.storageKey(), isMultipleSelection(), itemIds);
  };

  const recordSelection = (itemId: string): void => {
    persistSelections([itemId]);
  };

  const recordCheckboxSelection = (itemId: string, enabled: boolean): void => {
    setLastSelectedItemIds((current) => {
      const base =
        current.length > 0 ? current : getActiveCheckboxIds(options.items());
      const next = enabled
        ? [...new Set([...base, itemId])]
        : base.filter((id) => id !== itemId);
      writeStoredSelections(options.storageKey(), isMultipleSelection(), next);
      return next;
    });
  };

  const menuItems = createMemo((): ControlMenuItemType[] =>
    wrapItemsForSelectionTracking(options.items(), {
      recordSelection,
      recordCheckboxSelection,
      isMultipleSelection,
    })
  );

  const activateItem = (itemId: string): void => {
    const items = menuItems();
    const radioGroup = findRadioGroupForOptionId(items, itemId);
    if (radioGroup) {
      if (radioGroup.disabled) {
        return;
      }
      radioGroup.setValue(itemId);
      return;
    }

    const actionItem = findActionItemById(items, itemId);
    if (actionItem) {
      if (actionItem.disabled) {
        return;
      }
      actionItem.onClick();
      return;
    }

    const checkboxItem = findCheckboxItemById(items, itemId);
    if (checkboxItem) {
      if (checkboxItem.disabled) {
        return;
      }
      checkboxItem.setValue(true);
    }
  };

  const activateMultipleCheckboxSelection = (itemIds: string[]): void => {
    batch(() => {
      for (const item of menuItems()) {
        if (item.type !== 'checkbox') {
          continue;
        }

        if (item.value() ?? false) {
          item.setValue(false);
        }
      }

      for (const itemId of itemIds) {
        const checkboxItem = findCheckboxItemById(menuItems(), itemId);
        if (checkboxItem && !(checkboxItem.value() ?? false)) {
          checkboxItem.setValue(true);
        }
      }
    });
  };

  const activateLastSelection = (): void => {
    const items = options.items();

    if (isMultipleSelection()) {
      const storedItemIds = readStoredSelections(
        options.storageKey(),
        true
      ).filter((itemId) => isStoredMultipleSelectionItemId(items, itemId));
      const targetIds =
        storedItemIds.length > 0 ? storedItemIds : getActiveCheckboxIds(items);

      if (targetIds.length > 0) {
        activateMultipleCheckboxSelection(targetIds);
      }
      return;
    }

    const storedItemId = lastSelectedItemIds()[0] ?? null;
    const targetId =
      storedItemId && isReplayableItemId(items, storedItemId)
        ? storedItemId
        : getDefaultItemId(items);
    if (targetId) {
      activateItem(targetId);
    }
  };

  return { menuItems, activateLastSelection };
};
